import { type KeyboardEvent, type MouseEvent, type ReactElement, useState } from 'react';
import { r, s } from '@/theme/tokens';
import { Box } from '@/ui';
import { Menu, MenuItem } from '@/ui/primitives';
import {
  buildColourSwatchSelectorSwatchFrameSx,
  buildColourSwatchSelectorSwatchSx,
  colourSwatchSelectorFieldBackgroundColor,
  colourSwatchSelectorMenuItemSx,
} from './ColourSwatchSelector.styles';
import {
  colourSwatchSelectorControlSize,
  colourSwatchSelectorWidth,
} from './colourSwatchSelectorMetrics';

export type ColourSwatchOption<TValue extends string> = {
  value: TValue;
  swatch: string;
};

type ColourSwatchSelectorProps<TValue extends string> = {
  ariaLabel: string;
  disabled?: boolean;
  onChange: (value: TValue) => void;
  options: Array<ColourSwatchOption<TValue>>;
  value: TValue;
};

export const ColourSwatchSelector = <TValue extends string>({
  ariaLabel,
  disabled = false,
  onChange,
  options,
  value,
}: ColourSwatchSelectorProps<TValue>): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedOption = options.find((option) => option.value === value);
  const selectedSwatchSx = selectedOption
    ? buildColourSwatchSelectorSwatchSx(selectedOption)
    : { backgroundColor: 'transparent', borderColor: 'divider' };

  const handleOpenColorMenu = (event: MouseEvent<HTMLElement>): void => {
    if (disabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseColorMenu = (): void => {
    setAnchorEl(null);
  };

  const handleColorKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (disabled) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        onClick={handleOpenColorMenu}
        onKeyDown={handleColorKeyDown}
        sx={{
          minWidth: colourSwatchSelectorWidth,
          width: colourSwatchSelectorWidth,
          maxWidth: colourSwatchSelectorWidth,
          minHeight: colourSwatchSelectorControlSize,
          height: colourSwatchSelectorControlSize,
          maxHeight: colourSwatchSelectorControlSize,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: r('sm'),
          backgroundColor: colourSwatchSelectorFieldBackgroundColor,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Box component="span" sx={buildColourSwatchSelectorSwatchFrameSx(selectedSwatchSx)} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseColorMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: colourSwatchSelectorWidth,
              width: colourSwatchSelectorWidth,
              maxWidth: colourSwatchSelectorWidth,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${r('sm')} !important`,
              backgroundColor: colourSwatchSelectorFieldBackgroundColor,
              backgroundImage: 'none',
              boxShadow: 'none',
              overflow: 'hidden',
              clipPath: `inset(0 round ${r('sm')})`,
            },
          },
          list: {
            sx: {
              p: s('none'),
              m: s('none'),
              backgroundColor: colourSwatchSelectorFieldBackgroundColor,
            },
          },
        }}
      >
        {options.map((option) => {
          const optionSwatchSx = buildColourSwatchSelectorSwatchSx(option);
          return (
            <MenuItem
              key={option.value}
              onClick={() => {
                onChange(option.value);
                handleCloseColorMenu();
              }}
              sx={[
                colourSwatchSelectorMenuItemSx,
                { backgroundColor: value === option.value ? 'action.selected' : undefined },
              ]}
            >
              <Box component="span" sx={buildColourSwatchSelectorSwatchFrameSx(optionSwatchSx)} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
